import { css, html, LitElement } from 'lit'
import { customElement, query, state } from 'lit/decorators.js'
import { DealerSearchElementModel } from '../api/ContentDeliveryApi.schemas'
import "./BaiduMap"
import { dealersClient } from './Client2'
import './GeoLocation'
import './List'
import { DealerList } from './List'
import './Map'
import { DealerMap } from './Map'
import './Popup'
import './Search'
import { Search } from './Search'
import { Bounds, Country, Dealer, DealerProperties, MapSettings } from './Types'

export const HORIZONTAL_BREAKPOINT = 768
const minZoom = 8

@customElement('lindberg-dealer-search')
export class LindbergDealers extends LitElement {
	static styles = css`
	:host {
		display: block;
		font-family: inherit;
	}

	main {
		display: flex;
		flex-direction: column;
		position: relative;
	}

	lindberg-dealers-map, lindberg-dealers-baidu-map{
		position: sticky;
		top: 0;
		height: calc(100vh - var(--header-height) - 100px);
	}

	lindberg-dealers-list {
		position: relative;
	}

	lindberg-dealers-search {
		position: absolute;
		top: 10px;
		left: 10px;
		z-index: 1;
	}


	@media (min-width: ${HORIZONTAL_BREAKPOINT}px) {
		:host {
			height: calc(100vh - var(--header-height));
		}

		main {
			flex-direction: row;
			height: 100%;
		}

		lindberg-dealers-map, lindberg-dealers-baidu-map {
			order: 2;
			position: relative;
			flex: 1;
			height: 100%;
		}
		lindberg-dealers-list {
			order: 1;
			flex: 0 0 320px;
			max-width: 480px;
			overflow-y: auto;
		}
	}
`
	private _data!: { content: DealerSearchElementModel; settings: {}} 
	public get data(): { content: DealerSearchElementModel; settings: {}}  {
		return this._data
	}
	public set data(value: { content: DealerSearchElementModel; settings: {}} ) {
		this._data = value
		if(value.content.properties?.filter) this.settings.activeFilters?.push(value.content.properties?.filter)
	}

	@state()
	dealers: Dealer[] = []

	@state()
	visibleDealers: Dealer[] = []

	@state()
	selectedDealer: Dealer | null = null

	@state()
	selectedCountry: Country | null = null
	
	@state()
	filters: string[] = ['acetanium', 'air', 'horn', 'now', 'rim', 'spirit', 'strip', 'sun', 'tt', 'precious', 'kid', 'blok'] // todo: Get From Umbraco

	@state()
	settings: MapSettings = { language: document.documentElement.lang.toUpperCase(), worldView: 'us', provider: 'Mapbox', screenSize: window.innerWidth < HORIZONTAL_BREAKPOINT ? 'small' : 'large', useBaidu: false, activeFilters: [] }

	@query('lindberg-dealers-map')
	map?: DealerMap

	@query('lindberg-dealers-search')
	search?: Search

	@query('lindberg-dealers-list')
	list?: DealerList

	private mapBounds: Bounds | null = null
	private mapZoom = 0

	// private client = new DealerSearchClient({ baseUrl: 'https://lindberg.com' })	

	connectedCallback(): void {
		super.connectedCallback()
		this.setAttribute("version", "1.0.0")
		this.resize()
		this.addEventListener('resize', this.resize)
	}

	resize() {
		this.style.setProperty('--header-height', Math.min(120, this.offsetTop) + 'px')
		this.settings.screenSize = window.innerWidth < HORIZONTAL_BREAKPOINT ? 'small' : 'large'
	}

	setLanguage(language: string) {
		this.settings = { ...this.settings, language }
	}

	useBaidu(value:boolean){
		this.settings = {... this.settings, useBaidu: value}
	}

	render() {
		return html`
			<main @country-selected=${this.loadDealers} @dealer-selected=${this.showDealer} @location-selected=${this.loadDealers}>
				${this.settings.useBaidu ? 
					html`<lindberg-dealers-baidu-map .dealers=${this.visibleDealers} .selectedCountry=${this.selectedCountry} .selectedDealer=${this.selectedDealer} .settings=${this.settings} @map-moved=${this.mapMoved}>
							<lindberg-dealers-popup .selectedDealer=${this.selectedDealer} .settings=${this.settings}></lindberg-dealers-popup>
						</lindberg-dealers-baidu-map>` :
				html`
					<lindberg-dealers-geolocation .settings=${this.settings}></lindberg-dealers-geolocation>
					<lindberg-dealers-map .dealers=${this.visibleDealers} .selectedCountry=${this.selectedCountry} .selectedDealer=${this.selectedDealer} .settings=${this.settings} @map-moved=${this.mapMoved}>
						<lindberg-dealers-popup .selectedDealer=${this.selectedDealer} .settings=${this.settings}></lindberg-dealers-popup>
						<lindberg-dealers-search .settings=${this.settings} @filters-changed=${this.filtersChanged}></lindberg-dealers-search>
					</lindberg-dealers-map>`}
            	<lindberg-dealers-list .selectedCountry=${this.selectedCountry} .dealers=${this.visibleDealers} .selectedDealer=${this.selectedDealer} .settings=${this.settings}></lindberg-dealers-list>            	
			</main>
        `
	}

	filtersChanged() {
		this.updateVisibleDealers()
	}

	async mapMoved(e: CustomEvent) {
		// return;
		if (this.mapBounds &&
			this.mapZoom === e.detail.zoom &&
			this.mapBounds.north === e.detail.bounds.north &&
			this.mapBounds.south === e.detail.bounds.south &&
			this.mapBounds.east === e.detail.bounds.east &&
			this.mapBounds.west === e.detail.bounds.west
		) return

		const bounds = this.mapBounds = e.detail.bounds as Bounds
		this.mapZoom = e.detail.zoom
		this.updateVisibleDealers()

		if (e.detail.zoom >= minZoom) {
			const byBounds = await dealersClient.getByBoundsAndCountry(this.selectedCountry?.code!, bounds.north, bounds.east, bounds.south, bounds.west)
			byBounds.forEach(x => x.properties.minZoom = minZoom)
			const filtered = byBounds.filter(x => x.properties.countryCode == this.selectedCountry?.code && !this.dealers.find(y => y.properties.entryNo == x.properties.entryNo))
			this.dealers = [...this.dealers, ...filtered]
			// Map may have moved again while fetching
			if (bounds === this.mapBounds) this.updateVisibleDealers()
			// getDuplicateDealers(this.visibleDealers)
		}
	}

	updateVisibleDealers() {
		const bounds = this.mapBounds
		const zoom = this.mapZoom
		this.visibleDealers = this.dealers.filter(dealer => this.filterCollection(dealer)).filter(dealer =>
			!bounds ||
			dealer == this.selectedDealer ||
			(!dealer.properties.minZoom || dealer.properties.minZoom <= zoom) &&
			dealer.geometry.coordinates[1] <= bounds.north &&
			dealer.geometry.coordinates[1] >= bounds.south &&
			dealer.geometry.coordinates[0] >= bounds.west &&
			dealer.geometry.coordinates[0] <= bounds.east
		)
	}

	async showDealer(e: CustomEvent) {
		this.selectedDealer = e.detail.dealer
	}
	
	filterCollection(dealer:Dealer) {
		if (!this.settings.activeFilters || this.settings.activeFilters.length === 0) {
			return true;
		}
		const lowerFilters = this.settings.activeFilters.map(filter => filter.toLowerCase());
		const { isKidTeenDealer, isPreciousDealer, productLines } = dealer.properties;
		const lowerLines = (productLines ?? []).map(line => line.toLowerCase());

		return lowerFilters.some(filter => {
			switch (filter) {
				case "kidteen":
					return isKidTeenDealer;
				case "precious":
					return isPreciousDealer;
				default:
					return lowerLines.includes(filter);
			}
		});
	}


	async loadDealers(e?: CustomEvent) {
		console.log(e)
		if (e?.type == "location-selected" && e.detail.country) {
			this.selectedCountry = e.detail.country
			this.selectedDealer = null
			// this.map?.setCenter({latitude: e.detail.coordinates[1], longitude: e.detail.coordinates[0]}, 9)
			// this.map?.setBounds(e.detail.bounds)
			this.dealers = (await dealersClient.getByCountry(e.detail.country.code))
			if (e.detail.bounds) {
				// return
				const bounds = { west: e.detail.bounds[0], south: e.detail.bounds[1], east: e.detail.bounds[2], north: e.detail.bounds[3] }
				this.map?.setBounds(bounds)
				const byBounds = await dealersClient.getByBoundsAndCountry(this.selectedCountry?.code!, bounds.north, bounds.east, bounds.south, bounds.west)
				// console.log({byBounds})
				byBounds.forEach(x => x.properties.minZoom = minZoom)
				const filtered = byBounds.filter(x => x.properties.countryCode == this.selectedCountry?.code && !this.dealers.find(y => y.properties.entryNo == x.properties.entryNo))
				this.dealers = [...this.dealers, ...filtered]
				this.visibleDealers = this.dealers.filter(dealer => this.filterCollection(dealer)).filter(dealer =>
					dealer == this.selectedDealer ||
					byBounds.includes(dealer) ||
					// (!dealer.properties.minZoom || dealer.properties.minZoom <= e.detail.zoom) &&
					(dealer.properties.location.lat <= bounds.north &&
					dealer.properties.location.lat >= bounds.south &&
					dealer.properties.location.lon >= bounds.west &&
					dealer.properties.location.lon <= bounds.east)
				)
			} else {
				this.map?.setCenter({ latitude: e.detail.coordinates[1], longitude: e.detail.coordinates[0] }, 9)
			}
		} else if (e?.type == "country-selected") {
			this.dealers = (await dealersClient.getByCountry(e.detail.country.code))
			this.selectedCountry = e.detail.country
			this.map?.setBounds(e.detail.country.bounds!)
			this.selectedDealer = null
			this.visibleDealers = this.dealers.filter(dealer => this.filterCollection(dealer))
			console.log("Country selected:", e.detail.country)
		}
		// getDuplicateDealers(this.visibleDealers)
	}
}

function getDuplicateDealers(dealers: Dealer[]) {
	console.log('By homepage:', groupDuplicateProperty(dealers.filter(x => x.properties.homepage), "homepage"))
	console.log('By name:', groupDuplicateProperty(dealers, "name"))
}

function groupDuplicateProperty<F extends Dealer, K extends keyof DealerProperties>( features: F[], prop: K, keyFn: (value: DealerProperties[K]) => string = (v) => String(v),
): Map<string, F[]> {
  const groups = new Map<string, F[]>();
  for (const f of features) {
    const k = keyFn(f.properties![prop]!);
    (groups.get(k) ?? groups.set(k, []).get(k)!).push(f);
  }
  return new Map([...groups].filter(([, fs]) => fs.length > 1));
}

declare global {
	interface HTMLElementTagNameMap {
		'lindberg-dealers': LindbergDealers
	}
}
